import { useEffect, useRef, useState } from "react";
import {
  apiFetchLinkPreview,
  apiFetchLinkPreviewImageBlob,
  type LinkPreviewResponse,
} from "../lib/authApi";
import { readLinkPreviewCached, writeLinkPreviewCached } from "../lib/linkPreviewCache";

type LinkPreviewCardProps = {
  pageUrl: string;
};

export function LinkPreviewCard({ pageUrl }: LinkPreviewCardProps) {
  const objectUrlRef = useRef<string | null>(null);
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "stub" }
    | { kind: "ready"; data: LinkPreviewResponse; imageObjectUrl: string | null }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setState({ kind: "loading" });

    void (async () => {
      try {
        const fromDisk = await readLinkPreviewCached(pageUrl);
        if (fromDisk) {
          let imageObjectUrl: string | null = null;
          const { data, imageBytes, imageMime } = fromDisk;
          if (imageBytes && imageBytes.length > 0) {
            const mime = imageMime || "image/jpeg";
            const blob = new Blob([new Uint8Array(imageBytes)], { type: mime });
            imageObjectUrl = URL.createObjectURL(blob);
            objectUrlRef.current = imageObjectUrl;
          }
          if (cancelled) {
            if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
            return;
          }
          setState({ kind: "ready", data, imageObjectUrl });
          return;
        }

        const data = await apiFetchLinkPreview(pageUrl);
        if (cancelled) return;

        let imageObjectUrl: string | null = null;
        let imageBytes: Uint8Array | null = null;
        let imageMime: string | null = null;
        if (data.ok && data.imageProxyUrl) {
          try {
            const blob = await apiFetchLinkPreviewImageBlob(data.imageProxyUrl);
            if (cancelled) return;
            imageMime = blob.type || "image/jpeg";
            imageBytes = new Uint8Array(await blob.arrayBuffer());
            imageObjectUrl = URL.createObjectURL(new Blob([new Uint8Array(imageBytes)], { type: imageMime }));
            objectUrlRef.current = imageObjectUrl;
          } catch {
            /* no thumbnail */
          }
        }
        void writeLinkPreviewCached(pageUrl, data, imageBytes, imageMime).catch(() => {
          /* ignore cache write failures */
        });
        if (cancelled) {
          if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
          return;
        }
        setState({ kind: "ready", data, imageObjectUrl });
      } catch {
        if (!cancelled) setState({ kind: "stub" });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [pageUrl]);

  if (state.kind === "loading") {
    return (
      <div className="bubble__link-preview bubble__link-preview--loading" aria-hidden>
        …
      </div>
    );
  }

  if (state.kind === "stub") {
    const host = (() => {
      try {
        return new URL(pageUrl).hostname.replace(/^www\./, "");
      } catch {
        return pageUrl;
      }
    })();
    return (
      <a
        href={pageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bubble__link-preview bubble__link-preview--fallback"
      >
        <div className="bubble__link-preview-body">
          {host ? <div className="bubble__link-preview-host">{host}</div> : null}
          <div className="bubble__link-preview-title">{host || pageUrl}</div>
          <div className="bubble__link-preview-note">превью не загрузилось</div>
        </div>
      </a>
    );
  }

  const { data, imageObjectUrl } = state;
  if (!data.ok && !data.title && !data.description && !data.imageProxyUrl) {
    return null;
  }

  const title = data.title ?? data.siteName ?? pageUrl;
  const host = (() => {
    try {
      return new URL(pageUrl).hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  })();

  const isFallback = Boolean(data.fallback);

  return (
    <a
      href={pageUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`bubble__link-preview${isFallback ? " bubble__link-preview--fallback" : ""}`}
    >
      {imageObjectUrl ? (
        <div className="bubble__link-preview-img-wrap">
          <img src={imageObjectUrl} alt="" className="bubble__link-preview-img" loading="lazy" />
        </div>
      ) : null}
      <div className="bubble__link-preview-body">
        {host ? <div className="bubble__link-preview-host">{host}</div> : null}
        <div className="bubble__link-preview-title">{title}</div>
        {isFallback ? <div className="bubble__link-preview-note">только ссылка</div> : null}
        {data.description ? (
          <div className="bubble__link-preview-desc">{data.description}</div>
        ) : null}
      </div>
    </a>
  );
}
